<template>
  <div class="h-full flex justify-center align-center flex-col">
    <div class="absolute top-0 left-0 text-white text-xs px-2 z-50"></div>
    <ConfirmationPopup
      v-if="state.navigationConfirmationIsVisible"
      :text="`${t('leavingTalkwallWarning')} ${state.actualUrl}`"
      @confirm="navigateToUrl(state.actualUrl)"
      @close-popup="closeNavigationConfirmation()"
    />
    <ConfirmationPopup
      v-if="state.deleteNoteIsVisible"
      :text="t('deletionwarningnote')"
      @confirm="deleteNote()"
      @close-popup="toggleDeleteNoteConfirmationPopup()"
    />
    <ImagePopup v-if="state.noteToShow?.attachment" :note="state.noteToShow" @close-popup="() => (state.noteToShow = undefined)" />
    <div
      id="copy-wrapper"
      class="z-51 w-14 h-7 bg-slate-bright flex absolute right-16 bottom-5 cursor-pointer rounded-3xl"
      :class="copyStyles"
      @click="pasteNotes()"
      @touchstart.prevent="pasteNotes()"
    >
      <p class="absolute bottom-5 right-0 rounded-full font-bold text-xs bg-white w-5 h-5 flex justify-center items-center">
        {{ notePasteText }}
      </p>
      <div class="w-14 h-7 flex justify-center">
        <img class="w-5 invert" :src="AddCopy" alt="" />
      </div>
    </div>
    <button
      type="button"
      class="absolute bottom-5 right-4 h-8 w-8 flex bg-blue-middle rounded-full justify-center items-center cursor-pointer z-51 note-border"
      :aria-label="t('showTutorial')"
      @click="showTutorialPopup()"
      @touchstart.prevent="showTutorialPopup()"
    >
      <span class="absolute animate-ping motion-reduce:animate-none inline-flex h-8 w-8 rounded-full bg-sky-400 opacity-50"></span>
      <span class="z-101 text-white font-bold" aria-hidden="true">?</span>
    </button>

    <Tutorial v-if="state.tutorialIsVisible" @close="closeTutorialPopup" />
    <p class="sr-only" aria-live="polite">{{ announcement }}</p>

    <!-- The wall canvas is the visible area on screen -->
    <div
      id="wall-canvas"
      ref="wallCanvas"
      tabindex="0"
      role="region"
      :aria-label="t('boardRegion')"
      aria-describedby="board-keyboard-hint"
      class="h-screen w-screen overflow-hidden focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-[#4a90e2]"
      :class="{
        'border-2': state.highlightDropTarget,
      }"
      @touchmove="manageTouchMove"
      @keydown="onCanvasKeydown"
      @scroll="onCanvasScroll"
      @focusin="onCanvasFocusin"
    >
      <p id="board-keyboard-hint" class="sr-only">{{ t('boardKeyboardHint') }}</p>
      <!-- The note board will be larger than then wall canvas, as it usually can be scrolled around-->
      <div
        id="note-board"
        ref="noteBoard"
        class="zoomable origin-top-left bg-raster-pattern border-dashed border-blue-300"
        :class="{
          'opacity-75': sidebarNoteBeingDragged.note && !state.highlightDropTarget,
          //'bg-raster-pattern': !sidebarNoteBeingDragged.note,
        }"
        :style="{
          width: state.initialBoardSize.w + 'px',
          height: state.initialBoardSize.h + 'px',
          transform,
          borderWidth: `${4 * inverseScale}px`,
          //scale: zoomLevels[zoomIndex.value],
          //zoom: zoomLevels[zoomIndex.value],
          //transform: `scale(${state.currentZoomLevel})`,
        }"
        @pointerup="(e: PointerEvent) => noteDragEnd()"
      >
        <NoteVue
          v-for="note in placedNotes"
          :id="note._id"
          :key="note._id"
          tabindex="0"
          data-note
          class="rounded-xl absolute w-64 touch-none focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[6px] focus-visible:outline-[#4a90e2]"
          :class="{
            'cursor-grabbing': state.noteBeingDragged?.note,
          }"
          :style="{
            top: notePosition(note).y + 'px',
            left: notePosition(note).x + 'px',
            'z-index': state.lastClickedNote?._id === note._id ? 2 : 1,
            width: (liveTextLengths.get(note._id) ?? note.text.length) > 240 ? '80ch' : '',
          }"
          :note="note"
          :board-id="selectedBoardId"
          :zoom-level="zoomLevels[zoomIndex]"
          @pointerdown="(e: PointerEvent) => noteDragStart(e, note)"
          @pointermove="(e: PointerEvent) => noteDragMove(e)"
          @keydown="(e: KeyboardEvent) => onNoteKeydown(e, note._id)"
          @invoke-navigation-confirmation="invokeNavigationConfirmation"
          @delete-note="prepareNoteDeletion"
          @show-image-popup="showImagePopup"
          @live-text-length="updateLiveTextLength"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, reactive, ref, provide, onMounted, nextTick, PropType, onUnmounted, onUpdated, watch, toRefs } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { BoardEntry, Parcel, Note, Wall } from '@/common/classes'
  import { ParcelType, zoomLevels } from '@/constants'

  import { useNoteStore } from '@/composables/noteStore'
  import { useUserStore } from '@/composables/userStore'

  import useParcelStore from '@/composables/parcel'
  import useFilterStore from '@/composables/filter'
  import useCommonStore from '@/composables/common'
  import useNoteSelection from '@/composables/noteSelection'
  import { useBoardKeyboard } from '@/composables/useBoardKeyboard'

  import ConfirmationPopup from '@/components/popups/ConfirmationPopup.vue'
  import ImagePopup from '@/components/popups/ImagePopup.vue'
  import Tutorial from '@/components/popups/Tutorial.vue'
  import NoteVue from './NoteView.vue'

  import AddCopy from '@/assets/icons/fontawesome/paste-solid.svg'
  import { DraggedNote } from '@/views/pages/WallpageView.vue'
  import { useZoom, UseZoomOptions, ZoomContainerRef } from '@/composables/useZoom'

  interface DraggedBoard {
    dragging: boolean // Board is currently in motion
    pointer1?: PointerEvent // For mouse or first touch gesture
    pointer2?: PointerEvent // For second touch gesture
    key1?: KeyboardEvent // For keyboard events
  }

  const { t } = useI18n({})

  const openMenuNoteId = ref<string | null>(null)
  provide('openMenuNoteId', openMenuNoteId)

  const props = defineProps({
    wall: { type: Object as PropType<Wall>, required: true },
    notes: { type: Object as PropType<Note[]>, required: true },
    sidebarNoteBeingDragged: { type: Object as PropType<DraggedNote>, required: true },
    restrictedActionAllowed: { type: Boolean, required: true },
    sidebarValue: { type: Number, required: true },
    zoomIndex: { type: Number, required: true },
  })

  const liveTextLengths = reactive(new Map<string, number>())

  function updateLiveTextLength(noteId: string, length: number) {
    liveTextLengths.set(noteId, length)
  }

  const state = reactive({
    actualUrl: '',

    newBoardIsVisible: false,
    tutorialIsVisible: false,
    deleteNoteIsVisible: false,
    navigationConfirmationIsVisible: false,

    initialBoardSize: {
      w: 0,
      h: 0,
    }, // initial note board size
    currentZoomLevel: 1,
    scrollPoint: { x: 0, y: 0 }, // scroll offset
    didSmoothZoom: false,
    noteBeingDragged: undefined as DraggedNote | undefined,
    dragGroupIds: [] as string[], // notes moving with the dragged note: the selection, or just the note itself
    dragDelta: { x: 0, y: 0 }, // board pixels
    boardBeingDragged: { triggered: false, dragging: false } as DraggedBoard,

    noteToDelete: {} as Note | undefined,
    noteToShow: {} as Note | undefined,
    lastClickedNote: undefined as Note | undefined,
    previousMousePosition: { x: 0, y: 0 },
    highlightDropTarget: false,
    savedScale: -1,
  })

  const zoomOptions: UseZoomOptions = {
    minScale: 0.25,
    maxScale: 3,
    zoomSpeed: 0.1,
    enablePan: true,
    enableWheel: true,
    enableTouch: true, // Enable touch/pointer interactions
    constrainToContainer: true, // Prevent content positioning outside container
    allowScaleSmallerThanContainer: false, // Ensure content fills container
    touchSensitivity: 1.1, // Touch zoom sensitivity (0.5 = slower, 2 = faster)
  }
  const {
    scale,
    inverseScale,
    translateX,
    translateY,
    transform,
    bindToElements,
    resetZoom,
    zoomToScale,
    effectiveMinScale, // Shows the actual minimum scale being used
    isGestureActive, // Shows when touch gesture is active
    isDragging,
    canZoomIn,
    canZoomOut,
    containerToZoomable,
    injectPointer,
    panTo,
  } = useZoom(zoomOptions)

  const wallCanvas: ZoomContainerRef = ref<HTMLElement>()
  const noteBoard: ZoomContainerRef = ref<HTMLElement>()
  let resizeTimeout: NodeJS.Timeout

  defineExpose({
    noteBoard,
    sidebarNoteDragEnd,
    zoomToScale,
    canZoomIn,
    canZoomOut,
    scale,
  })

  const filter = useFilterStore()
  const noteStore = useNoteStore()
  const userStore = useUserStore()
  const parcelStore = useParcelStore()
  const noteSelection = useNoteSelection()

  // One timer per note, so moving several notes (or one after another) saves every one of them
  const noteSaveTimers = new Map<string, ReturnType<typeof setTimeout>>()
  let wallRect: DOMRect

  const { zoomIndex } = toRefs(props)

  function clamp(x: number, lo: number, hi: number) {
    return x < lo ? lo : x > hi ? hi : x
  }

  onMounted(async () => {
    await applyNoteBoardValueChange()
    window.addEventListener('resize', applyNoteBoardValueChange)
    setupCapturePhaseListeners()
    if (props.wall.status.lastContribution.getMilliseconds() > 604800000) showTutorialPopup() // more than 7 days have passed
  })

  onUnmounted(() => {
    window.removeEventListener('resize', applyNoteBoardValueChange)
    noteSelection.actions.clearSelection()
  })

  // Initial values. We configure the Note Board to be 4 times bigger than the Wall Canvas at zoom level 3 (100%)
  async function applyNoteBoardValueChange() {
    clearTimeout(resizeTimeout)
    const recalculate = async () => {
      if (wallCanvas.value && noteBoard.value) {
        wallRect = wallCanvas.value.getBoundingClientRect()
        state.initialBoardSize.h = wallRect.height * 4
        state.initialBoardSize.w = wallRect.width * 4
        await nextTick()
        bindToElements(wallCanvas.value, noteBoard.value)
      }
    }
    resizeTimeout = setTimeout(() => recalculate(), 300)
  }

  watch(zoomIndex, () => {
    zoom(zoomIndex.value)
  })

  const selectedBoardId = filter.getters.selectedBoardId
  watch(selectedBoardId, (boardId) => noteSelection.actions.setBoard(boardId), { immediate: true })

  const copyStyles = computed(() => {
    if (noteStore.getters.notesToCopy.value.length > 0) return numberOfPossiblePastes.value > 0 ? '' : 'opacity-60 cursor-not-allowed'
    else return 'hidden'
  })

  const notePasteText = computed(() => {
    if (noteStore.getters.notesToCopy.value.length > 0 && numberOfPossiblePastes.value === 0) return noteStore.getters.notesToCopy.value.length
    else return numberOfPossiblePastes.value
  })

  const placedNotes = computed(() => props.notes.filter((n) => n.entries && n.entries[selectedBoardId.value]))

  const { announcement, onCanvasKeydown, onNoteKeydown } = useBoardKeyboard({
    canvas: wallCanvas,
    translateX,
    translateY,
    scale,
    panTo,
    zoomToScale,
    resetZoom,
    moveSelection: (dx, dy) => moveNotes(noteSelection.getters.selectedNoteIds.value, dx, dy),
    t: (key, values) => t(key, values),
  })

  // Focusing an off-screen note makes the browser scroll the canvas. The board is positioned by transform,
  // so turn that scroll into a pan instead
  function onCanvasScroll() {
    const canvas = wallCanvas.value
    if (!canvas || (!canvas.scrollLeft && !canvas.scrollTop)) return
    const { scrollLeft, scrollTop } = canvas
    canvas.scrollLeft = 0
    canvas.scrollTop = 0
    panTo(translateX.value - scrollLeft, translateY.value - scrollTop)
  }

  function onCanvasFocusin(e: FocusEvent) {
    const target = e.target
    if (target instanceof HTMLElement && target.hasAttribute('data-note') && target.matches(':focus-visible')) {
      target.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    }
  }

  const numberOfPossiblePastes = computed(() => {
    const currentTaskId = filter.getters.selectedTaskId.value
    return noteStore.getters.notesToCopy.value.filter((note: Note) => note.task_id !== currentTaskId).length
  })

  // -------------------------------------------

  async function pasteNotes() {
    if (numberOfPossiblePastes.value > 0) {
      const currentTaskId = filter.getters.selectedTaskId.value
      const notes = noteStore.getters.notesToCopy.value.filter((note: Note) => note.task_id !== currentTaskId) // filter possible pastes
      for (const note of notes) {
        note.task_id = currentTaskId
        const res = await noteStore.actions.addNewNote(note)
        if (res.success && res.body) {
          const parcel = new Parcel({ parcelType: ParcelType.MessageCreate, body: res.body as Note }, userStore.getters.user.value, props.wall)
          await parcelStore.actions.sendParcel(props.wall._id, parcel)
        }
      }
      noteStore.actions.clearStagedCopies()
    }
  }

  function showTutorialPopup() {
    state.tutorialIsVisible = !state.tutorialIsVisible
  }

  function closeTutorialPopup() {
    state.tutorialIsVisible = false
  }

  // -----------  NOTE DRAG FUNCTIONS -----------

  // Track all active pointers at capture phase so we can detect two-finger
  // gestures even when the first pointer was captured by a note element.
  let noteDragPointerId: number | undefined
  let noteDragPointerType = '' // 'mouse', 'touch' or 'pen'; decides how a click selects
  const allPointerIds = new Set<number>()
  let gestureTransitionActive = false
  let boardPointerDown: { id: number; x: number; y: number } | undefined

  function setupCapturePhaseListeners() {
    const el = wallCanvas.value
    if (!el) return

    el.addEventListener(
      'pointerdown',
      (e: PointerEvent) => {
        allPointerIds.add(e.pointerId)
        boardPointerDown = allPointerIds.size === 1 ? { id: e.pointerId, x: e.clientX, y: e.clientY } : undefined

        if (allPointerIds.size >= 2 && state.noteBeingDragged && noteDragPointerId !== undefined) {
          // Two fingers are down while a note drag is active.
          // Cancel the note drag and hand both pointers to the wall
          // so it can run a pinch/pan gesture.
          const noteEl = state.noteBeingDragged.el
          try {
            noteEl?.releasePointerCapture(noteDragPointerId)
          } catch {
            /* pointer may already have been released */
          }
          resetNoteDrag()

          // Inject the first pointer (which the wall never saw because
          // noteDragStart stopped its propagation) into the wall's
          // active-pointer tracking.
          injectPointer(noteDragPointerId, state.previousMousePosition.x, state.previousMousePosition.y)
          noteDragPointerId = undefined

          // Prevent noteDragStart from firing for the second pointer
          // (if it landed on a note). The flag is cleared once the
          // gesture ends or in noteDragStart itself.
          gestureTransitionActive = true
        }
      },
      { capture: true },
    )

    el.addEventListener(
      'pointerup',
      (e: PointerEvent) => {
        allPointerIds.delete(e.pointerId)
        if (allPointerIds.size === 0) gestureTransitionActive = false
        // A click (not a pan) on empty board clears the selection
        const down = boardPointerDown
        boardPointerDown = undefined
        const onNote = e.target instanceof Element && !!e.target.closest('[data-note]')
        if (down && down.id === e.pointerId && !onNote && Math.hypot(e.clientX - down.x, e.clientY - down.y) < 5) {
          noteSelection.actions.clearSelection()
        }
      },
      { capture: true },
    )

    el.addEventListener(
      'pointercancel',
      (e: PointerEvent) => {
        allPointerIds.delete(e.pointerId)
        if (allPointerIds.size === 0) gestureTransitionActive = false
      },
      { capture: true },
    )
  }

  // Board pixel position, including the offset of an ongoing group drag
  function notePosition(note: Note) {
    const entry = note.entries[selectedBoardId.value]
    const x = (entry.xpos / 100) * state.initialBoardSize.w
    const y = (entry.ypos / 100) * state.initialBoardSize.h
    if (!state.dragGroupIds.includes(note._id)) return { x, y }
    return { x: x + state.dragDelta.x, y: y + state.dragDelta.y }
  }

  // How far (in percent of the board) a group of notes can move before one of them leaves the board
  function moveLimits(notes: Note[]) {
    const limits = { minX: -Infinity, maxX: Infinity, minY: -Infinity, maxY: Infinity }
    for (const note of notes) {
      const entry = note.entries[selectedBoardId.value]
      const rect = document.getElementById(note._id)?.getBoundingClientRect()
      const w = rect ? ((rect.width * inverseScale.value) / state.initialBoardSize.w) * 100 : 0
      const h = rect ? ((rect.height * inverseScale.value) / state.initialBoardSize.h) * 100 : 0
      limits.minX = Math.max(limits.minX, -entry.xpos)
      limits.maxX = Math.min(limits.maxX, 100 - w - entry.xpos)
      limits.minY = Math.max(limits.minY, -entry.ypos)
      limits.maxY = Math.min(limits.maxY, 100 - h - entry.ypos)
    }
    return limits
  }

  // Moves the notes together by percent of the board and schedules their saves. Returns how many moved
  function moveNotes(noteIds: string[], dxPercent: number, dyPercent: number): number {
    const notes = placedNotes.value.filter((n) => noteIds.includes(n._id))
    if (notes.length === 0) return 0
    const limits = moveLimits(notes)
    const dx = clamp(dxPercent, limits.minX, Math.max(limits.minX, limits.maxX))
    const dy = clamp(dyPercent, limits.minY, Math.max(limits.minY, limits.maxY))
    if (!dx && !dy) return 0
    for (const note of notes) {
      const entry = note.entries[selectedBoardId.value]
      entry.xpos += dx
      entry.ypos += dy
      scheduleNoteSave(note)
    }
    return notes.length
  }

  function scheduleNoteSave(note: Note) {
    const boardId = selectedBoardId.value
    clearTimeout(noteSaveTimers.get(note._id))
    noteSaveTimers.set(
      note._id,
      setTimeout(
        async () => {
          noteSaveTimers.delete(note._id)
          const entry = note.entries[boardId]
          const updatedNote = new Note(note)
          updatedNote.updateBoardEntry(updatedNote.entries[boardId], [entry.ypos, entry.xpos])
          await noteStore.actions.updateNote(updatedNote, props.wall._id, ParcelType.MessageBoard)
          const parcel = new Parcel({ parcelType: ParcelType.MessageBoard, body: updatedNote }, userStore.getters.user.value, props.wall)
          parcelStore.actions.sendParcel(props.wall._id, parcel)
        },
        parseInt(import.meta.env.VITE_NOTE_MOVE_TIMEOUT) || 1500,
      ),
    )
  }

  let dragLimits = { minX: 0, maxX: 0, minY: 0, maxY: 0 } // board pixels

  function resetNoteDrag() {
    state.noteBeingDragged = undefined
    state.dragGroupIds = []
    state.dragDelta = { x: 0, y: 0 }
  }

  function noteDragStart(event: PointerEvent, note: Note) {
    if (isDragging.value) return
    if (isGestureActive.value) return
    if (gestureTransitionActive) return
    state.lastClickedNote = note
    event.stopImmediatePropagation()

    if (event.shiftKey || event.metaKey || event.ctrlKey) {
      noteSelection.actions.toggleNote(note._id)
      return
    }

    const el = document.getElementById(note._id)
    if (el && noteBoard.value) {
      el.setPointerCapture(event.pointerId) // Element 'target' will receive/capture further events
      noteDragPointerId = event.pointerId
      noteDragPointerType = event.pointerType
      // Dragging a selected note drags the whole selection; an unselected note drags alone and stays unselected
      state.dragGroupIds = noteSelection.getters.isSelected(note._id) ? [...noteSelection.getters.selectedNoteIds.value] : [note._id]
      state.dragDelta = { x: 0, y: 0 }
      const limits = moveLimits(placedNotes.value.filter((n) => state.dragGroupIds.includes(n._id)))
      dragLimits = {
        minX: (limits.minX / 100) * state.initialBoardSize.w,
        maxX: (Math.max(limits.minX, limits.maxX) / 100) * state.initialBoardSize.w,
        minY: (limits.minY / 100) * state.initialBoardSize.h,
        maxY: (Math.max(limits.minY, limits.maxY) / 100) * state.initialBoardSize.h,
      }
      const position = notePosition(note)
      state.noteBeingDragged = { note, x: position.x, y: position.y, el }
      state.previousMousePosition = { x: event.clientX, y: event.clientY }
    }
  }

  function noteDragMove(event: PointerEvent) {
    if (isDragging.value) return
    if (isGestureActive.value) return
    if (state.boardBeingDragged.dragging) return
    if (state.noteBeingDragged && noteBoard.value && !state.boardBeingDragged.dragging) {
      event.preventDefault()

      const newY = event.clientY // relative to VIEWPORT
      const newX = event.clientX // relative to VIEWPORT

      // Difference between previous and current pointer accounting for scale
      const diffY = (newY - state.previousMousePosition.y) * inverseScale.value
      const diffX = (newX - state.previousMousePosition.x) * inverseScale.value

      state.previousMousePosition = { x: newX, y: newY }
      state.dragDelta.y = clamp(state.dragDelta.y + diffY, dragLimits.minY, dragLimits.maxY)
      state.dragDelta.x = clamp(state.dragDelta.x + diffX, dragLimits.minX, dragLimits.maxX)
    }
  }

  function noteDragEnd() {
    noteDragPointerId = undefined
    if (state.boardBeingDragged.dragging) return
    if (state.noteBeingDragged && state.noteBeingDragged.note && !state.boardBeingDragged.dragging) {
      const dx = (state.dragDelta.x / state.initialBoardSize.w) * 100
      const dy = (state.dragDelta.y / state.initialBoardSize.h) * 100
      const noteId = state.noteBeingDragged.note._id
      // Below the threshold the press counts as a click. A mouse click selects only this note (Shift adds); touch has
      // no Shift, so once something is selected a tap adds or removes the note, like a phone's photo gallery.
      const tapAddsToSelection = noteDragPointerType !== 'mouse' && noteSelection.getters.selectedCount.value > 0
      if (Math.abs(dy) > 0.5 || Math.abs(dx) > 0.5) moveNotes(state.dragGroupIds, dx, dy)
      else if (tapAddsToSelection) noteSelection.actions.toggleNote(noteId)
      else noteSelection.actions.selectOnly(noteId)
      resetNoteDrag()
    }
  }

  async function sidebarNoteDragEnd(event: PointerEvent, note: Note) {
    try {
      const targetNote = note
      if (targetNote && noteBoard.value) {
        const ctz = containerToZoomable(event.clientX, event.clientY)
        const x = clamp(ctz.x - 150, 0, state.initialBoardSize.w - 256)
        const y = clamp(ctz.y - 100, 0, state.initialBoardSize.h - 150)

        let boardEntry = {
          xpos: (x / state.initialBoardSize.w) * 100,
          ypos: (y / state.initialBoardSize.h) * 100,
          highlightColor: '',
          wall_id: selectedBoardId.value,
          creator: targetNote.status.createdBy,
        } as BoardEntry

        if (targetNote.entries === undefined) targetNote.entries = {} // reset to empty object
        targetNote.addBoardEntry(boardEntry.wall_id, boardEntry)
        const res = await noteStore.actions.updateNote(targetNote, props.wall._id, ParcelType.MessageBoard)
        if (res) {
          const parcel = new Parcel({ parcelType: ParcelType.MessageBoard, body: targetNote }, userStore.getters.user.value, props.wall)
          await parcelStore.actions.sendParcel(props.wall._id, parcel)
        }
      }
    } catch (e) {
      console.error(e)
    }
  }

  // -----------  BOARD DRAG FUNCTIONS -----------

  // If we are dragging a note to the board, we need to prevent scroll and iOS "bounce" activity with the touchmove event
  function manageTouchMove(event: TouchEvent) {
    if (state.boardBeingDragged.dragging) {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
    }
  }

  // Zoom using the zoomIndex (discreet values)
  async function zoom(toZoomIndex: number) {
    if (toZoomIndex >= 0 && toZoomIndex < zoomLevels.length - 1) {
      // If we adjusted the zoomIndex due to a 'smooth' zoom, we skip setting the zoom here
      if (state.didSmoothZoom) {
        state.didSmoothZoom = false
        return
      }
      state.currentZoomLevel = zoomLevels[toZoomIndex]
    }
  }

  function prepareNoteDeletion(note: Note) {
    state.noteToDelete = note
    toggleDeleteNoteConfirmationPopup()
  }

  async function deleteNote() {
    if (state.noteToDelete) {
      let note = new Note(state.noteToDelete)
      const res = await noteStore.actions.updateNote(note, props.wall._id, ParcelType.MessageDelete)
      if (res.success) {
        const parcel = new Parcel({ parcelType: ParcelType.MessageDelete, body: note }, userStore.getters.user.value, props.wall)
        await parcelStore.actions.sendParcel(props.wall._id, parcel)
        if (state.noteToDelete) noteStore.actions.localDelete(state.noteToDelete)
        state.noteToDelete = undefined
      }
    }
  }

  function toggleDeleteNoteConfirmationPopup() {
    state.deleteNoteIsVisible = !state.deleteNoteIsVisible
  }

  function invokeNavigationConfirmation(url: string) {
    state.actualUrl = url
    state.navigationConfirmationIsVisible = true
  }

  function closeNavigationConfirmation() {
    state.navigationConfirmationIsVisible = false
  }

  function navigateToUrl(url: string) {
    window.open(url)
  }

  function showImagePopup(note: Note) {
    state.noteToShow = note
  }
</script>

<style scoped>
  .max-height-25 {
    max-height: 25%;
  }

  .max-height-50 {
    max-height: 50%;
  }

  .zoomable {
    position: relative;
    transition: transform;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 20px;
    box-sizing: border-box;
  }
</style>
